import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MODULE_ORDER, CLINIC_TYPES } from "@/lib/modules";
import {
  Plus,
  Edit,
  Power,
  Building2,
  X,
  Save,
  RefreshCw,
  Boxes,
  CreditCard,
  GitBranch,
  Settings2,
  UsersRound,
} from "lucide-react";

function RelieversTab({ hospital }) {
  const [doctors, setDoctors] = useState([]);
  const [sel, setSel] = useState("");
  const [relIds, setRelIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!hospital?.id) return;
    try {
      const { data } = await api.get(`/platform/hospitals/${hospital.id}/relievers`);
      setDoctors(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail || e)); }
  };
  useEffect(() => { load(); setSel(""); setRelIds([]); /* eslint-disable-next-line */ }, [hospital?.id]);

  const selectDoc = (id) => {
    setSel(id);
    const d = doctors.find((x) => x.id === id);
    setRelIds(d?.reliever_ids || []);
  };
  const toggle = (id) => setRelIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.put(`/platform/hospitals/${hospital.id}/doctors/${sel}/relievers`, { reliever_ids: relIds });
      toast.success("Relievers updated");
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail || e)); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><UsersRound size={17} /> Relievers</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Assign relievers for {hospital?.hospital_name}. Relievers cover each other and can see each other&apos;s patients (mutual).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {doctors.length === 0 ? (
          <div className="text-sm text-slate-400">No doctors in this hospital.</div>
        ) : (
          <>
            <div className="max-w-md">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Doctor</div>
              <select value={sel} onChange={(e) => selectDoc(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="platform-reliever-doctor">
                <option value="">Select a doctor…</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
              </select>
            </div>
            {sel ? (
              <>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Relievers</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[45vh] overflow-auto pr-1">
                    {doctors.filter((d) => d.id !== sel).map((d) => {
                      const checked = relIds.includes(d.id);
                      return (
                        <button key={d.id} type="button" onClick={() => toggle(d.id)}
                          className={`rounded-xl border p-3 text-left ${checked ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-900 text-sm truncate">{d.name || d.email}</div>
                              <div className="text-xs text-slate-500 truncate">{d.email}</div>
                            </div>
                            {checked ? <Badge className="bg-blue-700">Reliever</Badge> : <Badge variant="outline">Add</Badge>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Button onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="platform-save-relievers">
                  <Save size={14} className="mr-1" /> Save Relievers
                </Button>
              </>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}


function HospitalForm({ initial, onSubmit, onCancel, submitLabel }) {
  const [form, setForm] = useState({
    hospital_name: initial?.hospital_name || "",
    address: initial?.address || "",
    contact_number: initial?.contact_number || "",
    email: initial?.email || "",
    status: initial?.status || "active",
    clinic_type: initial?.clinic_type || "medical",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hospital_name.trim()) {
      toast.error("Hospital name is required");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(form);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2">
        <Label>Hospital Name *</Label>
        <Input data-testid="hosp-name" value={form.hospital_name} onChange={(e) => set("hospital_name", e.target.value)} required />
      </div>
      <div className="md:col-span-2">
        <Label>Address</Label>
        <Input data-testid="hosp-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
      </div>
      <div>
        <Label>Contact Number</Label>
        <Input data-testid="hosp-contact" value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} />
      </div>
      <div>
        <Label>Email</Label>
        <Input data-testid="hosp-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
      </div>
      <div>
        <Label>Status</Label>
        <select
          data-testid="hosp-status"
          value={form.status}
          onChange={(e) => set("status", e.target.value)}
          className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div>
        <Label>Clinic Type</Label>
        <select
          data-testid="hosp-clinic-type"
          value={form.clinic_type}
          onChange={(e) => set("clinic_type", e.target.value)}
          className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
        >
          {CLINIC_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <div className="text-[11px] text-slate-500 mt-1">
          Dental clinics get the dental space and are provisioned with the dental module on.
        </div>
      </div>
      <div className="md:col-span-2 flex justify-end gap-2 mt-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>}
        <Button type="submit" disabled={busy} data-testid="btn-submit-hospital">
          {busy ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function AssignAdminForm({ hospital, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/platform/hospital-admins", {
        hospital_id: hospital.id,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      toast.success(`Admin created for ${hospital.hospital_name}`);
      onDone();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <Label>Full Name *</Label>
        <Input data-testid="admin-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
      </div>
      <div>
        <Label>Email *</Label>
        <Input data-testid="admin-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
      </div>
      <div>
        <Label>Password *</Label>
        <Input data-testid="admin-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required />
      </div>
      <div className="md:col-span-3 flex justify-end gap-2">
        <Button type="submit" disabled={busy} data-testid="btn-create-admin">{busy ? "Saving…" : "Create Hospital Admin"}</Button>
      </div>
    </form>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-900">{value ?? "—"}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
          {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function ModulesTab({ hospital }) {
  const [payload, setPayload] = useState(null);
  const [modules, setModules] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!hospital?.id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/platform/hospitals/${hospital.id}/modules`);
      setPayload(data);
      setModules(data?.modules || {});
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [hospital?.id]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/platform/hospitals/${hospital.id}/modules`, { modules });
      setPayload(data);
      setModules(data?.modules || {});
      toast.success("Modules saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const labels = payload?.labels || {};
  const orderedKeys = [
    ...MODULE_ORDER.filter((k) => Object.prototype.hasOwnProperty.call(modules, k)),
    ...Object.keys(modules).filter((k) => !MODULE_ORDER.includes(k)),
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Modules</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={busy}><RefreshCw size={14} className="mr-1" />Refresh</Button>
          <Button size="sm" onClick={save} disabled={busy}><Save size={14} className="mr-1" />Save</Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {orderedKeys.map((key) => {
            const enabled = !!modules[key];
            return (
              <div key={key} className={`rounded-xl border p-4 ${enabled ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-slate-900">{labels[key] || key.replaceAll("_", " ")}</div>
                    <div className="text-xs font-mono text-slate-500 mt-1">{key}</div>
                  </div>
                  <Switch checked={enabled} onCheckedChange={() => setModules((m) => ({ ...m, [key]: !m[key] }))} />
                </div>
                <Badge className={`mt-3 ${enabled ? "bg-emerald-600" : ""}`} variant={enabled ? "default" : "secondary"}>
                  {enabled ? "Enabled" : "Off"}
                </Badge>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function SubscriptionTab({ hospital }) {
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!hospital?.id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/platform/hospitals/${hospital.id}/license`);
      setPayload(data);
      setForm(data?.license || {});
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [hospital?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/platform/hospitals/${hospital.id}/license`, form);
      setPayload(data);
      setForm(data?.license || {});
      toast.success("Subscription saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Subscription</CardTitle>
        <Button size="sm" onClick={save} disabled={busy}><Save size={14} className="mr-1" />Save</Button>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Plan</Label>
          <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.plan || "starter"} onChange={(e) => set("plan", e.target.value)}>
            <option value="starter">Starter</option>
            <option value="clinic_pro">Clinic Pro</option>
            <option value="medical_center">Medical Center</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div>
          <Label>Status</Label>
          <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.subscription_status || "trial"} onChange={(e) => set("subscription_status", e.target.value)}>
            <option value="trial">Trial</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div>
          <Label>Subscription End</Label>
          <Input type="date" value={(form.subscription_end || "").slice(0, 10)} onChange={(e) => set("subscription_end", e.target.value)} />
        </div>
        <div>
          <Label>User Limit</Label>
          <Input type="number" value={form.user_limit || 5} onChange={(e) => set("user_limit", Number(e.target.value))} />
        </div>
        <div>
          <Label>Branch Limit</Label>
          <Input type="number" value={form.branch_limit || 1} onChange={(e) => set("branch_limit", Number(e.target.value))} />
        </div>
        <div>
          <Label>Storage Limit MB</Label>
          <Input type="number" value={form.storage_limit_mb || 1024} onChange={(e) => set("storage_limit_mb", Number(e.target.value))} />
        </div>
        <div className="md:col-span-3">
          <Label>Notes</Label>
          <Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </CardContent>
    </Card>
  );
}

function BranchesTab({ hospital }) {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ branch_name: "", address: "", contact_number: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!hospital?.id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/platform/hospitals/${hospital.id}/branches`);
      setBranches(data?.branches || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [hospital?.id]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.branch_name.trim()) {
      toast.error("Branch name is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/platform/hospitals/${hospital.id}/branches`, form);
      toast.success("Branch created");
      setForm({ branch_name: "", address: "", contact_number: "", email: "" });
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Create Branch</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Branch Name</Label>
              <Input value={form.branch_name} onChange={(e) => setForm((f) => ({ ...f, branch_name: e.target.value }))} />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            </div>
            <div>
              <Label>Contact</Label>
              <Input value={form.contact_number} onChange={(e) => setForm((f) => ({ ...f, contact_number: e.target.value }))} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <Button type="submit" disabled={busy}><Plus size={14} className="mr-1" />Create Branch</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Branches</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 border-b">
              <tr>
                <th className="p-3 text-left">Branch</th>
                <th className="p-3 text-left">Address</th>
                <th className="p-3 text-left">Contact</th>
                <th className="p-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="p-3 font-medium">{b.branch_name}{b.is_main ? <Badge className="ml-2" variant="secondary">Main</Badge> : null}</td>
                  <td className="p-3">{b.address || "—"}</td>
                  <td className="p-3">{b.contact_number || b.email || "—"}</td>
                  <td className="p-3">{b.status || "active"}</td>
                </tr>
              ))}
              {branches.length === 0 ? <tr><td className="p-6 text-center text-slate-400" colSpan="4">No branches yet.</td></tr> : null}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PlatformHospitals() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [tab, setTab] = useState("overview");

  const selected = useMemo(() => hospitals.find((h) => h.id === selectedId) || hospitals[0], [hospitals, selectedId]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/hospitals");
      const rows = data || [];
      setHospitals(rows);
      if (!selectedId && rows[0]?.id) setSelectedId(rows[0].id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const create = async (form) => {
    try {
      await api.post("/platform/hospitals", form);
      toast.success("Hospital created");
      setShowCreate(false);
      refresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const save = async (form) => {
    try {
      await api.put(`/platform/hospitals/${editing.id}`, form);
      toast.success("Hospital updated");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const toggleStatus = async (h) => {
    const next = h.status === "active" ? "inactive" : "active";
    if (!window.confirm(`${next === "active" ? "Activate" : "Deactivate"} ${h.hospital_name}?`)) return;
    try {
      await api.put(`/platform/hospitals/${h.id}/status`, { status: next });
      toast.success(`Hospital ${next}`);
      refresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const hardDelete = async (h) => {
    if (h.status !== "inactive") {
      toast.error("Deactivate the hospital first before deleting permanently.");
      return;
    }
    const typed = window.prompt(
      `This will PERMANENTLY DELETE "${h.hospital_name}" and ALL its data ` +
      `(patients, prescriptions, medicines, queue, referrals, branches, etc).\n\n` +
      `This cannot be undone.\n\nType the hospital name to confirm:`
    );
    if (typed == null) return; // cancel
    if (typed.trim() !== h.hospital_name.trim()) {
      toast.error("Hospital name did not match. Delete cancelled.");
      return;
    }
    try {
      const { data } = await api.delete(`/platform/hospitals/${h.id}?hard=true`);
      toast.success(`Hospital permanently deleted (${data?.mode || "hard"})`);
      setSelectedId("");
      refresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: Building2 },
    { key: "modules", label: "Modules", icon: Settings2 },
    { key: "relievers", label: "Relievers", icon: UsersRound },
    { key: "branches", label: "Branches", icon: GitBranch },
    { key: "subscription", label: "Subscription", icon: CreditCard },
  ];

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Hospital Workspace</h1>
          <p className="text-sm text-slate-500">Provision hospitals and manage modules, branches, subscriptions, and operations.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="btn-new-hospital"><Plus size={16} className="mr-2" />New Hospital</Button>
      </div>

      {showCreate && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Create Hospital</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setShowCreate(false)}><X size={16} /></Button>
          </CardHeader>
          <CardContent>
            <HospitalForm onSubmit={create} onCancel={() => setShowCreate(false)} submitLabel="Create Hospital" />
          </CardContent>
        </Card>
      )}

      {editing && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Edit · {editing.hospital_name}</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setEditing(null)}><X size={16} /></Button>
          </CardHeader>
          <CardContent>
            <HospitalForm initial={editing} onSubmit={save} onCancel={() => setEditing(null)} submitLabel="Save Changes" />
          </CardContent>
        </Card>
      )}

      {assignFor && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Assign Hospital Admin · {assignFor.hospital_name}</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setAssignFor(null)}><X size={16} /></Button>
          </CardHeader>
          <CardContent>
            <AssignAdminForm hospital={assignFor} onDone={() => { setAssignFor(null); refresh(); }} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hospitals</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading…</div>
            ) : hospitals.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">
                <Building2 size={28} className="mx-auto mb-2 text-slate-400" />
                No hospitals yet.
              </div>
            ) : (
              <div className="divide-y max-h-[72vh] overflow-auto" data-testid="platform-hospitals-list">
                {hospitals.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => { setSelectedId(h.id); setTab("overview"); }}
                    className={`w-full text-left p-4 hover:bg-slate-50 ${selected?.id === h.id ? "bg-indigo-50" : ""}`}
                  >
                    <div className="font-medium text-slate-900">{h.hospital_name}</div>
                    <div className="text-xs text-slate-500 mt-1">{h.address || h.email || "—"}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={h.status === "active" ? "default" : "secondary"} className={h.status === "active" ? "bg-emerald-600" : ""}>
                        {h.status}
                      </Badge>
                      <span className="text-xs text-slate-500">{h.patient_count || 0} patients</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-5">
          {selected ? (
            <>
              <Card>
                <CardContent className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-semibold text-slate-900">{selected.hospital_name}</h2>
                      <Badge variant={selected.status === "active" ? "default" : "secondary"} className={selected.status === "active" ? "bg-emerald-600" : ""}>
                        {selected.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-500 mt-1">{selected.address || selected.email || "No address set"}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAssignFor(selected)}>Assign Admin</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(selected)}><Edit size={14} className="mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleStatus(selected)} className={selected.status === "active" ? "text-amber-700" : "text-emerald-700"}>
                      <Power size={14} className="mr-1" />{selected.status === "active" ? "Deactivate" : "Activate"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => hardDelete(selected)}
                      disabled={selected.status === "active"}
                      title={selected.status === "active" ? "Deactivate first to enable permanent delete" : "Permanently delete this hospital and all its data"}
                      className="text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-40"
                      data-testid="btn-hard-delete-hospital"
                    >
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-2">
                {tabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <Button key={t.key} variant={tab === t.key ? "default" : "outline"} onClick={() => setTab(t.key)}>
                      <Icon size={14} className="mr-2" />{t.label}
                    </Button>
                  );
                })}
              </div>

              {tab === "overview" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard icon={Building2} label="Status" value={selected.status || "active"} sub={selected.code || selected.id} />
                  <StatCard icon={Boxes} label="Patients" value={selected.patient_count || 0} sub="Current tenant count" />
                  <StatCard icon={CreditCard} label="Users" value={selected.user_count || 0} sub="Assigned users" />
                </div>
              )}

              {tab === "modules" && <ModulesTab hospital={selected} />}
              {tab === "relievers" && <RelieversTab hospital={selected} />}
              {tab === "branches" && <BranchesTab hospital={selected} />}
              {tab === "subscription" && <SubscriptionTab hospital={selected} />}
            </>
          ) : (
            <Card><CardContent className="p-10 text-center text-slate-500">Select or create a hospital.</CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}
